import { ForwardedRef, forwardRef, useCallback, useRef } from 'react'
import { cn } from '~utils/cn'

import Icon from '~components/ui/icon'
import TextInputHelper, { TextInputHelperProps } from './@base/helper'
import { InputPrimitiveProps, useCustomizedInputProps } from './@base/hooks'
import TextInputLabel, { TextInputLabelProps } from './@base/label'

type InputType = 'outlined' | 'underlined'

type InputProps = {
  label: TextInputLabelProps['children']
  helperMessage: TextInputHelperProps['helperMessage']
  showHelper: boolean
  showMaxLength: boolean
  error: boolean
  onDelete: () => void
  /**
   * input tag의 attribute 중 type이 이미 있어서 inputType으로 네이밍했습니다.
   */
  inputType?: InputType
}

export type TextFieldProps = Readonly<InputPrimitiveProps & Partial<InputProps>>

/**
 * @version v1.2.2
 * @author Maggie
 * @description TextInputHelper 컴포넌트에 readOnly prop 제거
 */
const TextField = forwardRef(
  (
    {
      label,
      helperMessage,
      showHelper = false,
      showMaxLength = false,
      error,
      onDelete,
      inputType = 'outlined',
      ...props
    }: TextFieldProps,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const internalRef = useRef<HTMLInputElement>(null)
    const { inputProps, isFocused } = useCustomizedInputProps<HTMLInputElement, 'input'>(props)
    const { value, disabled, readOnly, maxLength, className } = inputProps

    const valueLength = value?.toString()?.length
    const showDeleteIcon = !!(valueLength && isFocused && onDelete && !error)

    const _onDelete = useCallback(() => {
      if (internalRef.current) {
        internalRef.current.focus()
        internalRef.current.value = ''
      }
      onDelete?.()
    }, [onDelete])

    return (
      <div className={cn('flex flex-col', className)}>
        {label && (
          <TextInputLabel isFocused={isFocused} isError={error} isDisabled={disabled}>
            {label}
          </TextInputLabel>
        )}
        <div className="relative w-full">
          <input
            {...inputProps}
            ref={ref ?? internalRef}
            className={cn(
              'text-body-large w-full border-none text-gray-900 outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500',
              'transition-colors motion-reduce:transition-none',
              'disabled:cursor-not-allowed',
              inputType === 'outlined' &&
                cn(
                  'rounded-xl dark:border-solid border border-gray-300 dark:text-white bg-white dark:bg-gray-900 dark:border-gray-500 py-4 pl-4 pr-10',
                  {
                    'text-gray-300': disabled || readOnly,
                  },
                  error && '!border-solid !border-extended-error !border-[2px]'
                ),
              inputType === 'underlined' &&
                cn('bg-background rounded-none border-b-2 border-solid border-gray-300 py-3 pr-7', {
                  'border-primary-600': isFocused,
                  'border-red-100': error,
                  'bg-transparent': disabled || readOnly,
                }),
              !showDeleteIcon && 'pr-4'
            )}
          />
          <Icon
            name="remove_circle_solid"
            className={cn(
              'absolute top-1/2 m-auto h-6 w-6 -translate-y-1/2 cursor-pointer fill-gray-900',
              showDeleteIcon ? 'block cursor-pointer' : 'hidden cursor-default',
              {
                'right-3': inputType === 'outlined',
                'bottom-1 right-0': inputType === 'underlined',
              }
            )}
            /**
             * onClick 이벤트를 사용하면, input의 onBlur 이벤트가 먼저 발생해서 콜백 함수 트리거가 되지 않습니다.
             */
            onMouseDown={_onDelete}
            onTouchStart={_onDelete}
          />
        </div>
        {showHelper && (
          <TextInputHelper
            helperMessage={helperMessage}
            maxLength={maxLength}
            showMaxLength={showMaxLength}
            valueLength={valueLength}
            isError={error}
            isDisabled={disabled}
            readOnly={readOnly}
            className={cn({
              'mt-1': inputType === 'outlined',
              'mt-2': inputType === 'underlined',
            })}
          />
        )}
      </div>
    )
  }
)

TextField.displayName = 'TextField'
export default TextField
